import {
  Box,
  InputProps,
  MenuItem,
  TextField,
  TextFieldProps,
} from '@mui/material';
import _ from 'lodash';
import {useEffect, useLayoutEffect, useRef, useState} from 'react';

export type SingleMultiSelectProps = Pick<
  TextFieldProps,
  | 'size'
  | 'disabled'
  | 'fullWidth'
  | 'label'
  | 'error'
  | 'helperText'
  | 'InputProps'
  | 'SelectProps'
  | 'sx'
> & {
  value?: number | number[] | string | string[] | null;
  options: {id: number | string; name: string}[];
  multiple?: boolean;
  nullLabel?: string;
  maxDefaultWidth?: number | null;
  onChange?: (v: number | number[] | string | string[]) => void;
};

export const SingleMultiSelect = ({
  maxDefaultWidth,
  nullLabel,
  ...props
}: SingleMultiSelectProps) => {
  const propValue = props.value;
  // This variable is needed only for multiple mode
  const [selected, setSelected] = useState<number[]>(
    props.multiple ? (props.value as number[]) : []
  );

  const prevValueRef = useRef(propValue);
  const prevWidthRef = useRef<number | null>(null);
  const labelContainerRef = useRef<HTMLDivElement>(null);
  const [maxWidth, setMaxWidth] = useState<number | undefined>(undefined);

  const options = props.options;

  useEffect(() => {
    if (_.isEqual(prevValueRef.current, propValue)) {
      return;
    }
    prevValueRef.current = propValue;

    if (props.multiple && Array.isArray(propValue)) {
      setSelected(propValue as number[]);
    } else if (
      !props.multiple &&
      propValue !== null &&
      propValue !== undefined &&
      propValue !== 'all'
    ) {
      setSelected([propValue] as number[]);
    } else {
      setSelected([]);
    }
  }, [propValue, props.multiple]);

  const renderValue = (selectedItems: any) => {
    if (!selectedItems || selectedItems.length === 0) {
      return nullLabel;
    }

    if (selectedItems.length === 1 && selectedItems[0] === 'all') {
      return nullLabel;
    }

    const array = Array.isArray(selectedItems)
      ? selectedItems
      : [selectedItems];

    const label = array
      .map((id) => options.find((o) => o.id === id)?.name ?? id)
      .join(', ');

    return (
      <Box
        ref={labelContainerRef}
        sx={{
          width: '100%',
          maxWidth: maxWidth ?? maxDefaultWidth ?? '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={label}
      >
        {label}
      </Box>
    );
  };

  const fieldValue = props.multiple
    ? selected?.length > 0
      ? selected
      : ['all']
    : selected[0] ?? 'all';

  const handleChange: InputProps['onChange'] = (event: any) => {
    // For single mode we return value immediately
    if (!props.multiple) {
      return props.onChange?.(
        event.target.value === 'all' ? null : event.target.value
      );
    }

    // For multiple mode we need to update array of selected items
    // and return them later when dropdown is closed
    const isSelectAll = _.last(event.target.value) === 'all';
    setSelected(
      isSelectAll ? [] : event.target.value.filter((i: any) => i !== 'all')
    );
  };

  const handleClose = () => {
    // For multiple mode we return value only after dropdown is closed
    if (props.multiple) {
      props.onChange?.(selected);
    }
  };

  useLayoutEffect(() => {
    if (labelContainerRef.current) {
      const currentWidth = labelContainerRef.current.offsetWidth;

      if (currentWidth) {
        if (prevWidthRef.current === null) {
          prevWidthRef.current = currentWidth;
          setMaxWidth(currentWidth);
        } else if (prevWidthRef.current > currentWidth) {
          setMaxWidth(currentWidth);
          prevWidthRef.current = currentWidth;
        }
      }
    }
  }, [fieldValue]);

  return (
    <TextField
      {...props}
      label={props.label || 'Items'}
      value={fieldValue}
      select
      SelectProps={{
        renderValue,
        multiple: props.multiple,
        onClose: handleClose,
        MenuProps: {
          sx: {maxHeight: '500px'},
        },
      }}
      onChange={handleChange}
    >
      <MenuItem value="all">{nullLabel ?? 'All Items'}</MenuItem>
      {options.map((option) => (
        <MenuItem key={option.id ?? ''} value={option.id ?? ''}>
          {option.name}
        </MenuItem>
      ))}
    </TextField>
  );
};
